import { Component, EventEmitter, Input, Output } from '@angular/core';
import { PoSelectOption } from '@po-ui/ng-components';

import {
  PERIOD_PRESET_OPTIONS,
  Period,
  PeriodPreset,
  defaultPeriod,
  resolvePresetRange
} from '../period.util';

/** Valor do po-datepicker-range (datas em ISO). */
interface DatepickerRangeValue {
  start?: string;
  end?: string;
}

/**
 * Seletor de período reutilizável (presets + intervalo personalizado).
 * Emite `periodChange` sempre que o recorte muda, para a tela recarregar os dados.
 */
@Component({
  selector: 'app-period-filter',
  templateUrl: './period-filter.component.html',
  styleUrls: ['./period-filter.component.css']
})
export class PeriodFilterComponent {
  readonly presetOptions: PoSelectOption[] = PERIOD_PRESET_OPTIONS;

  /**
   * `true` para os campos entrarem na `po-row` de quem chama, em vez de o componente
   * abrir uma linha só sua.
   *
   * Existe porque o recorte nem sempre é a linha inteira: no painel de custos ele divide
   * a linha com área, usuário e tipo de review, e uma linha própria empurraria os quatro
   * campos para baixo. Junto com o `display: contents` do host (ver o CSS), os campos
   * viram filhos diretos da `po-row` de fora e as classes de grid deles voltam a valer.
   */
  @Input() inline = false;

  /**
   * Classes de grid de cada campo. São entrada porque quem decide largura é a linha em
   * que o filtro está: sozinho ele ocupa o que precisar, dividindo espaço com outros
   * filtros ele tem que caber no que sobrou.
   */
  @Input() presetClass = 'po-sm-12 po-md-4 po-lg-3';
  @Input() rangeClass = 'po-sm-12 po-md-5 po-lg-4';

  @Input()
  get period(): Period { return this.internalPeriod; }
  set period(value: Period) {
    this.internalPeriod = value ? { ...value } : defaultPeriod();
    this.range = { start: this.internalPeriod.dateFrom, end: this.internalPeriod.dateTo };
  }

  @Output() periodChange = new EventEmitter<Period>();

  private internalPeriod: Period = defaultPeriod();
  range: DatepickerRangeValue = { start: this.internalPeriod.dateFrom, end: this.internalPeriod.dateTo };

  get preset(): PeriodPreset { return this.internalPeriod.preset; }
  set preset(value: PeriodPreset) { this.internalPeriod = { ...this.internalPeriod, preset: value }; }

  get isCustom(): boolean { return this.internalPeriod.preset === 'custom'; }

  /** Preset alterado: resolve o range (exceto custom) e emite. */
  onPresetChange(preset: PeriodPreset): void {
    if (preset === 'custom') {
      this.internalPeriod = { ...this.internalPeriod, preset };
      return; // aguarda o usuário escolher o intervalo
    }
    const range = resolvePresetRange(preset);
    this.internalPeriod = { preset, ...range };
    this.range = { start: range.dateFrom, end: range.dateTo };
    this.periodChange.emit({ ...this.internalPeriod });
  }

  /** Intervalo personalizado alterado: normaliza para YYYY-MM-DD e emite quando completo. */
  onRangeChange(): void {
    const dateFrom = this.normalize(this.range?.start);
    const dateTo = this.normalize(this.range?.end);
    this.internalPeriod = { preset: 'custom', dateFrom, dateTo };
    if (dateFrom && dateTo) {
      this.periodChange.emit({ ...this.internalPeriod });
    }
  }

  private normalize(value?: string): string {
    if (!value) { return ''; }
    return value.length > 10 ? value.slice(0, 10) : value;
  }
}
